import { Box, Spacer } from '@chakra-ui/layout'
import {
  Collapse,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useOutsideClick,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { delay, isEmpty } from 'lodash'
import { useRef, useState } from 'react'
import { type APIUser } from '../../api'
import { useUserAwareTranslation } from '../../i18n/hooks'
import { ButtonPrimary } from '../Base'
import { ImageCard } from '../Cards'
import HtmlContainer from '../HtmlContainer'
import Title from '../Title'
import ImageForm from './ImageForm'
import UserForm from './UserForm'
import type { UploadedFile } from './common'
// eslint-disable-next-line @typescript-eslint/no-empty-function
function doNothing(): void {}

export interface UploadFilesData {
  subject: string
  author: string
  uploaderName?: string
  uploaderPosition?: string
}
export interface UploadFilesProps {
  data?: UploadFilesData
  user: APIUser
  isOpen: boolean
  onClose: () => void
  reloadHandler: () => void
}

const UploadFiles = (props: UploadFilesProps) => {
  const { data, user, isOpen = false, onClose = doNothing, reloadHandler = doNothing } = props
  const ref = useRef(null)

  useOutsideClick({
    ref: ref,
    handler: doNothing, // close()
  })

  const { t } = useUserAwareTranslation(user)
  const [step, setStep] = useState(0)
  const [uploadedFiles, setUploadedFiles] = useState([] as UploadedFile[])
  const [error, setError] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)

  return (
    <>
      {isCompleted && !isEmpty(uploadedFiles) ? (
        <>
          <Box color="gray.500" fontSize={'14px'} mb={10}>
            <HtmlContainer
              html={error ? t('save-error-description') : t('save-success-description')}
            />
          </Box>

          <Wrap>
            {uploadedFiles.map((e) => (
              <WrapItem key={e.id} w={['100%', '270px']}>
                <ImageCard
                  user={user}
                  withActions={false}
                  id={e.id}
                  title={e.title}
                  image={e.image}
                  subject={e.subject}
                  otherProvider={e.otherProvider}
                />
              </WrapItem>
            ))}
          </Wrap>
          <Flex direction={['column', 'row']} mb={4}>
            <Spacer />
            <ButtonPrimary
              mt={4}
              w={['100%', '190px']}
              reverse={false}
              onClick={() => {
                onClose()
                setUploadedFiles([])
                setIsCompleted(false)
                delay(reloadHandler, 3000)
              }}>
              {t('ok')}
            </ButtonPrimary>
          </Flex>
        </>
      ) : (
        <>
          <Collapse in={isOpen} animateOpacity ref={ref}>
            <Title text={step === 0 ? t('add-image-title') : t('finalize-title')} />
            <>
              <Tabs index={step} isLazy colorScheme="red">
                <TabList mb="1em" borderColor="gray.300" color="gray.300">
                  <Tab>{t('add-image-tab')}</Tab>
                  <Tab isDisabled={step === 0}>{t('validate-tab')}</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <ImageForm
                      {...{
                        data,
                        user,
                        onClose: () => {
                          onClose()
                          setUploadedFiles([])
                        },
                        setStep,
                        setFiles: setUploadedFiles,
                      }}
                    />
                  </TabPanel>

                  <TabPanel>
                    <UserForm
                      {...{
                        data,
                        user,
                        onClose: () => {
                          onClose()
                          setUploadedFiles([])
                        },
                        setStep,
                        setError,
                        setIsCompleted,
                        uploadedFiles,
                      }}
                    />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </>
          </Collapse>
        </>
      )}
    </>
  )
}

export default UploadFiles
